/**
 * useOwnProfile — PPL-395.
 *
 * All data-loading for /profile in one hook; Profile.tsx calls nothing else.
 *
 * Four round-trips on mount, fired in parallel and all independent:
 *   1. getMyProfile(uid)                    → Firestore users/{uid}
 *   2. getUserStats({ includeContent })     → getUserStatsOptimizedEurW
 *   3. getUserBadges(uid)                   → getUserBadgesEurW
 *   4. getProfileViewCount()                → getProfileViewsEurW (countOnly)
 *
 * Conversations are lazy (opened by the messages tab). The "to review" count
 * costs nothing: it is read from the already-mounted WritebackProvider, which
 * is what keeps that number the same on the rail, on /insights and here.
 *
 * Nothing here throws to the page: a stats/badges/views failure degrades to a
 * zeroed value, and only a failed profile read sets `error`. A stats failure
 * additionally raises `statsError` so the page can distinguish "no activity"
 * from "the activity did not load", and a failed page-2 fetch leaves the
 * cursor and hasMore alone rather than silently ending the list.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useWriteback } from '../contexts/WritebackContext';
import { getMyProfile } from '../services/accountService';
import { getUserBadges } from '../services/badgeService';
import { getConversations } from '../services/messageService';
import {
  getProfileViewCount,
  getUserStats,
  emptyStatsPage,
  type OwnPost,
  type OwnReply,
  type ReplyCursor,
} from '../services/ownProfileService';
import type { EditableProfile } from '../types/account';
import type { Conversation, UserBadges } from '../types/social';

const PAGE_SIZE = 10;
const CONVERSATION_PAGE = 20;

export interface OwnProfileCounts {
  posts: number;
  replies: number;
  likes: number;
  views: number;
}

export interface UseOwnProfileResult {
  /** users/{uid} read fresh — NOT the login-time useAuth snapshot. */
  profile: Partial<EditableProfile> | null;
  uid: string | null;

  counts: OwnProfileCounts;

  badges: UserBadges | null;
  badgesLoading: boolean;

  /** useWriteback().pendingTotal — the same number the rail badge shows. */
  pendingReviewCount: number;

  /**
   * Always true while the page is open: the viewer IS the user, and useAuth's
   * heartbeat has just written lastAppOpen. Kept as a field so a real presence
   * source can replace it later without touching the JSX.
   */
  online: boolean;

  /** true until profile + the first stats call have both settled. */
  loading: boolean;
  /** Lowercase, human-readable; only set for a failure that empties the page. */
  error: string | null;
  /**
   * The stats call did not come back. The page still renders (with zeros), but
   * it must SAY the activity failed to load — zeros alone read as a brand-new
   * account, and this call scans every post and reply the user has, so a
   * timeout on a busy account is the realistic failure.
   */
  statsError: boolean;

  posts: OwnPost[];
  postsLoading: boolean;
  hasMorePosts: boolean;
  loadMorePosts: () => void;

  replies: OwnReply[];
  repliesLoading: boolean;
  hasMoreReplies: boolean;
  loadMoreReplies: () => void;

  conversations: Conversation[];
  conversationsLoading: boolean;
  /** The callable reported more than one page; the page links to /messages. */
  hasMoreConversations: boolean;
  /** Idempotent — a no-op once loaded or in flight. */
  loadConversations: () => void;

  loadingMore: boolean;
  reload: () => void;
}

const NO_COUNTS: OwnProfileCounts = { posts: 0, replies: 0, likes: 0, views: 0 };

export function useOwnProfile(): UseOwnProfileResult {
  const { user } = useAuth();
  const uid: string | null = user?.uid ?? null;
  const { pendingTotal } = useWriteback();

  const [profile, setProfile] = useState<Partial<EditableProfile> | null>(null);
  const [counts, setCounts] = useState<OwnProfileCounts>(NO_COUNTS);
  const [badges, setBadges] = useState<UserBadges | null>(null);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState(false);

  const [posts, setPosts] = useState<OwnPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(false);

  const [replies, setReplies] = useState<OwnReply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [hasMoreReplies, setHasMoreReplies] = useState(false);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);

  const [reloadToken, setReloadToken] = useState(0);

  // Cursors live in refs: they are read inside callbacks and must never make a
  // callback identity churn mid-pagination.
  const postCursor = useRef<string | null>(null);
  const replyCursor = useRef<ReplyCursor | null>(null);
  const conversationsRequested = useRef(false);
  // Ids already on screen. The server silently drops the offset when the cursor
  // doc no longer exists (a deleted post) and re-serves page 1, so every append
  // is de-duplicated against these.
  const postIds = useRef<Set<string>>(new Set());
  const replyIds = useRef<Set<string>>(new Set());

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // ─── Mount / reload load ──────────────────────────────────────────────
  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setBadgesLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setBadgesLoading(true);
    setError(null);
    setStatsError(false);
    // A reload re-opens the messages tab too: the latch is what makes
    // loadConversations idempotent, so it has to be released here.
    conversationsRequested.current = false;

    async function load(userId: string) {
      const [profileRes, statsRes, badgesRes, viewsRes] = await Promise.allSettled([
        getMyProfile(userId),
        getUserStats({ userId, includeContent: true, limit: PAGE_SIZE }),
        getUserBadges(userId),
        getProfileViewCount(),
      ]);

      if (cancelled) return;

      if (profileRes.status === 'fulfilled') {
        setProfile(profileRes.value);
      } else {
        setProfile(null);
        setError("couldn't load your profile — try again in a moment");
      }

      const page = statsRes.status === 'fulfilled' ? statsRes.value : emptyStatsPage(true);
      setStatsError(page.failed);
      setPosts(page.posts);
      setReplies(page.replies);
      setHasMorePosts(page.hasMorePosts);
      setHasMoreReplies(page.hasMoreReplies);
      postCursor.current = page.lastPostDoc;
      replyCursor.current = page.lastReplyDoc;
      postIds.current = new Set(page.posts.map((p) => p.id));
      replyIds.current = new Set(page.replies.map((r) => r.id));

      setCounts({
        posts: page.stats.totalPosts || page.posts.length,
        replies: page.stats.totalReplies || page.replies.length,
        likes: page.stats.totalLikesReceived || 0,
        views: viewsRes.status === 'fulfilled' ? viewsRes.value : 0,
      });

      setBadges(badgesRes.status === 'fulfilled' ? badgesRes.value : null);
      setBadgesLoading(false);
      setLoading(false);
    }

    void load(uid);

    return () => {
      cancelled = true;
    };
  }, [uid, reloadToken]);

  // ─── Pagination ───────────────────────────────────────────────────────
  const loadMorePosts = useCallback(() => {
    const cursor = postCursor.current;
    if (!uid || !cursor || postsLoading || !hasMorePosts) return;
    setPostsLoading(true);
    getUserStats({ userId: uid, includeContent: true, limit: PAGE_SIZE, postsOffset: cursor })
      .then((page) => {
        if (!alive.current) return;
        // A failed page must not end the list: keep the cursor and the button.
        if (page.failed) return;
        const fresh = page.posts.filter((p) => !postIds.current.has(p.id));
        fresh.forEach((p) => postIds.current.add(p.id));
        if (fresh.length) setPosts((prev) => [...prev, ...fresh]);
        // Nothing new means the server re-served a page we already have —
        // asking again would only repeat it.
        setHasMorePosts(page.hasMorePosts && fresh.length > 0);
        postCursor.current = page.lastPostDoc;
      })
      .finally(() => {
        if (alive.current) setPostsLoading(false);
      });
  }, [uid, postsLoading, hasMorePosts]);

  const loadMoreReplies = useCallback(() => {
    const cursor = replyCursor.current;
    if (!uid || !cursor || repliesLoading || !hasMoreReplies) return;
    setRepliesLoading(true);
    // The cursor object goes back verbatim — the server normalises {id: ...}.
    getUserStats({ userId: uid, includeContent: true, limit: PAGE_SIZE, repliesOffset: cursor })
      .then((page) => {
        if (!alive.current) return;
        if (page.failed) return;
        const fresh = page.replies.filter((r) => !replyIds.current.has(r.id));
        fresh.forEach((r) => replyIds.current.add(r.id));
        if (fresh.length) setReplies((prev) => [...prev, ...fresh]);
        setHasMoreReplies(page.hasMoreReplies && fresh.length > 0);
        replyCursor.current = page.lastReplyDoc;
      })
      .finally(() => {
        if (alive.current) setRepliesLoading(false);
      });
  }, [uid, repliesLoading, hasMoreReplies]);

  // ─── Conversations (lazy, one page) ───────────────────────────────────
  const loadConversations = useCallback(() => {
    if (!uid || conversationsRequested.current) return;
    conversationsRequested.current = true;
    setConversationsLoading(true);
    getConversations(CONVERSATION_PAGE)
      .then((res) => {
        if (!alive.current) return;
        // Same guard the Messages page applies (Messages.tsx): a request I have
        // not answered belongs to the requests flow, where the decision comes
        // before the thread — it must not appear here as an ordinary
        // conversation whose preview can be read and whose tap marks it seen.
        setConversations(
          res.conversations.filter(
            (c) => !(c.status === 'requested' && c.requestedBy && c.requestedBy !== uid),
          ),
        );
        setHasMoreConversations(res.hasMore);
      })
      .catch(() => {
        if (!alive.current) return;
        // Release the latch: a failed load must stay retryable, otherwise the
        // tab reads "no conversations yet." for the life of the mount.
        conversationsRequested.current = false;
        setConversations([]);
        setHasMoreConversations(false);
      })
      .finally(() => {
        if (alive.current) setConversationsLoading(false);
      });
    // reloadToken is a dependency so that reload() gives this callback a new
    // identity and the messages tab's effect re-fires after the latch reset.
  }, [uid, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  return {
    profile,
    uid,
    counts,
    badges,
    badgesLoading,
    pendingReviewCount: pendingTotal,
    online: true,
    loading,
    error,
    statsError,
    posts,
    postsLoading,
    hasMorePosts,
    loadMorePosts,
    replies,
    repliesLoading,
    hasMoreReplies,
    loadMoreReplies,
    conversations,
    conversationsLoading,
    hasMoreConversations,
    loadConversations,
    loadingMore: postsLoading || repliesLoading,
    reload,
  };
}

export default useOwnProfile;
